import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReviewerAndEmployee } from '../../../types'
import { PerModal } from '../../../components/custom_components/Modal'
import PerText from '../../../components/custom_components/Text'
import { deleteReviewerOfEmployeeRelation } from '../../../graphql/reviewerEmployee/hooks/useMutateReviewerOfEmployee'
import { showNotification } from '../../../components/custom_components/Notification/notification'

export interface DeleteReviewerModalProps {
  onClose: () => void
  relationToDelete: ReviewerAndEmployee | null
  companyId?: string
  buId?: string
}

export const DeleteReviewerModal: FC<DeleteReviewerModalProps> = ({ onClose, relationToDelete, companyId, buId }) => {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleDelete = async () => {
    if (!relationToDelete) return

    setIsLoading(true)
    setErrorMessage('')

    const result = await deleteReviewerOfEmployeeRelation(relationToDelete.id, companyId, buId)

    if (!!result.data && !result.errors) {
      showNotification({
        type: 'success',
        message: t('companySettingsView.reviewerTab.deleteModal.notifications.delete.success.title'),
        description: t('companySettingsView.reviewerTab.deleteModal.notifications.delete.success.description'),
      })
      resetAndClose()
    } else {
      const errorKey = result.errors?.[0].extensions?.validationErrors?.[0]?.errorKey
      const errorMessage = errorKey?.startsWith('error.code')
        ? result.errors?.[0].extensions?.validationErrors?.[0]?.message || t('companySettingsView.reviewerTab.deleteModal.error')
        : t('companySettingsView.reviewerTab.deleteModal.error')

      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetAndClose = () => {
    setErrorMessage('')
    setIsLoading(false)
    onClose()
  }

  if (!relationToDelete) return null

  return (
    <PerModal
      title={{ heading: t('companySettingsView.reviewerTab.deleteModal.title') }}
      size="medium"
      centered
      visible={!!relationToDelete}
      onCancel={resetAndClose}
      footer={{
        cancelBtn: {
          onClick: resetAndClose,
        },
        confirmBtn: {
          onClick: handleDelete,
          disabled: isLoading || !!errorMessage,
          isProcessing: { active: isLoading },
        },
      }}
    >
      <PerText fontSize="m" fontWeight="bold" mb="xxs">
        {t('companySettingsView.reviewerTab.deleteModal.description1')}
      </PerText>
      <PerText fontSize="m"> {t('companySettingsView.reviewerTab.deleteModal.description2')}</PerText>

      {errorMessage && (
        <PerText color="error" fontSize="m" mt="m">
          {errorMessage}
        </PerText>
      )}
    </PerModal>
  )
}

export default DeleteReviewerModalProps
